import React from 'react';
import { X, Shield, Lock, Globe, Database, Calendar, AlertTriangle, AlertCircle, Users, Building, ExternalLink } from 'lucide-react';
import { SearchResult } from '@/lib/api/types';

interface SearchResultsModalProps {
    isOpen: boolean;
    onClose: () => void;
    searchQuery: string;
    results: SearchResult[];
    isSearching: boolean;
    error?: string | null;
}

const SearchResultsModal: React.FC<SearchResultsModalProps> = ({
                                                                   isOpen,
                                                                   onClose,
                                                                   searchQuery,
                                                                   results,
                                                                   isSearching,
                                                                   error
                                                               }) => {
    if (!isOpen) return null;

    const handleBookConsultation = () => {
        window.open(
            'https://outlook.office.com/book/BUSINESSCONSULTATION@shieldtechub.onmicrosoft.com/?ismsaljsauthenabled',
            '_blank',
            'noopener,noreferrer'
        );
    };

    const getRiskColor = (risk: string) => {
        switch (risk) {
            case 'Low': return 'text-success-600 bg-success-50 border-success-200 dark:text-success-400 dark:bg-success-950 dark:border-success-800';
            case 'Medium': return 'text-warning-600 bg-warning-50 border-warning-200 dark:text-warning-400 dark:bg-warning-950 dark:border-warning-800';
            case 'High': return 'text-error-600 bg-error-50 border-error-200 dark:text-error-400 dark:bg-error-950 dark:border-error-800';
            case 'Critical': return 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-950 dark:border-red-700';
            default: return 'text-text-muted-light bg-background-light-secondary border-border-light dark:text-text-muted-dark dark:bg-background-dark-secondary dark:border-border-dark-subtle';
        }
    };

    const getRiskIcon = (risk: string) => {
        switch (risk) {
            case 'Critical': return AlertTriangle;
            case 'High': return AlertTriangle;
            case 'Medium': return Shield;
            case 'Low': return Shield;
            default: return Shield;
        }
    };

    const getPasswordStrengthColor = (strength: string) => {
        switch (strength) {
            case 'Excellent': return 'text-green-600 dark:text-green-400';
            case 'Good': return 'text-blue-600 dark:text-blue-400';
            case 'Fair': return 'text-yellow-600 dark:text-yellow-400';
            case 'Poor': return 'text-red-600 dark:text-red-400';
            default: return 'text-text-muted-light dark:text-text-muted-dark';
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" onClick={onClose} />

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-5xl bg-background-light dark:bg-background-dark-elevated border border-border-light dark:border-border-dark-subtle rounded-2xl shadow-2xl transform transition-all duration-300">

                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark-subtle">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-gradient-start to-gradient-end rounded-full flex items-center justify-center">
                                <Database className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                                    Security Exposure Report
                                </h2>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                    Threat intelligence analysis for <span className="font-semibold text-primary-500">{searchQuery}</span>
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-full text-text-muted-light dark:text-text-muted-dark hover:bg-background-light-elevated dark:hover:bg-background-dark-secondary transition-colors duration-200">
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="p-6">
                        {error ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-red-50 dark:bg-red-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-xl font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                                    Search Failed
                                </h3>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">{error}</p>

                                {/* CTA for error state */}
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <button onClick={onClose} className="px-6 py-2 text-white font-medium bg-gradient-to-r from-gradient-start to-gradient-end rounded-lg hover:shadow-lg transition-all duration-200">
                                        Try Again
                                    </button>
                                    <button
                                        onClick={handleBookConsultation}
                                        className="px-6 py-2 text-primary-600 dark:text-primary-400 font-medium border border-primary-500 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-950/20 transition-all duration-200 flex items-center gap-2"
                                    >
                                        Get Expert Help
                                        <ExternalLink className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ) : isSearching ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gradient-to-r from-gradient-start to-gradient-end rounded-full flex items-center justify-center mx-auto mb-6">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                </div>
                                <h3 className="text-xl font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                                    Scanning Dark Web Sources
                                </h3>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                    Analyzing breach databases, stealer logs, and underground forums...
                                </p>
                            </div>
                        ) : results.length > 0 ? (
                            <div className="space-y-8">
                                {results.map((result, index) => (
                                    <div key={index} className="space-y-6">
                                        {/* Header with risk assessment */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-4">
                                                <div>
                                                    <h3 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark">
                                                        {result.query}
                                                    </h3>
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        <Calendar className="h-4 w-4 text-text-muted-light dark:text-text-muted-dark" />
                                                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                                            Last compromise: {result.lastSeen}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`flex items-center space-x-2 px-4 py-2 rounded-full border font-medium ${getRiskColor(result.riskLevel)}`}>
                                                {React.createElement(getRiskIcon(result.riskLevel), { className: "h-4 w-4" })}
                                                <span>{result.riskLevel} Risk</span>
                                            </div>
                                        </div>

                                        {/* Primary stats grid */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle hover:border-primary-500/50 transition-all duration-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Total Infections</span>
                                                    <Shield className="h-5 w-5 text-red-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                                                    {result.totalInfections.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-text-muted-light dark:text-text-muted-dark">Malware infections</div>
                                            </div>

                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle hover:border-primary-500/50 transition-all duration-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Employees</span>
                                                    <Building className="h-5 w-5 text-blue-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                                                    {result.employeesCompromised.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-text-muted-light dark:text-text-muted-dark">Staff compromised</div>
                                            </div>

                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle hover:border-primary-500/50 transition-all duration-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Users</span>
                                                    <Users className="h-5 w-5 text-green-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                                                    {result.usersCompromised.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-text-muted-light dark:text-text-muted-dark">Users affected</div>
                                            </div>

                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle hover:border-primary-500/50 transition-all duration-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">Exposed URLs</span>
                                                    <Globe className="h-5 w-5 text-orange-500" />
                                                </div>
                                                <div className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
                                                    {result.exposedUrls.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-text-muted-light dark:text-text-muted-dark">Login pages found</div>
                                            </div>
                                        </div>

                                        {/* Additional insights */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Password Strength */}
                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle">
                                                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
                                                    <Lock className="h-4 w-4 text-purple-500" />
                                                    Password Security
                                                </h4>
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Strength Score:</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                                                                {result.passwordStrengthScore}
                                                            </span>
                                                            <span className="text-xs text-text-muted-light dark:text-text-muted-dark">/100</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Overall Rating:</span>
                                                        <span className={`font-semibold ${getPasswordStrengthColor(result.passwordStrength)}`}>
                                                            {result.passwordStrength}
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                        <div
                                                            className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 h-2 rounded-full transition-all duration-500"
                                                            style={{ width: `${result.passwordStrengthScore}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Top Threat */}
                                            <div className="bg-background-light-elevated dark:bg-background-dark-secondary p-6 rounded-xl border border-border-light dark:border-border-dark-subtle">
                                                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark mb-4 flex items-center gap-2">
                                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                                    Primary Threat
                                                </h4>
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Top Stealer Family:</span>
                                                        <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                                                            {result.topStealerFamily || 'Unknown'}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Compromised:</span>
                                                        <span className="text-xl font-bold text-red-600 dark:text-red-400">
                                                            {result.totalCompromised.toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-text-muted-light dark:text-text-muted-dark">
                                                        Combined employees and users affected
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Call to Action Banner - For Results Found */}
                                        <div className="bg-gradient-to-r from-primary-500/10 to-gradient-end/10 border border-primary-500/20 rounded-xl p-6">
                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                <div className="text-center sm:text-left">
                                                    <h4 className="text-lg font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                                                        Need Help Securing Your Organization?
                                                    </h4>
                                                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                                        Our cybersecurity experts provide continuous monitoring of your organization's security posture and deliver detailed forensic analysis.
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={handleBookConsultation}
                                                    className="px-8 py-3 bg-gradient-to-r from-gradient-start to-gradient-end text-white font-semibold rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
                                                >
                                                    Book Free Consultation
                                                    <ExternalLink className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-success-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Shield className="h-8 w-8 text-success-500" />
                                </div>
                                <h3 className="text-xl font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                                    No Threats Detected
                                </h3>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                                    No compromised data found for <span className="font-semibold">{searchQuery}</span> in our database.
                                </p>
                                <div className="bg-background-light-elevated dark:bg-background-dark-secondary border border-border-light dark:border-border-dark-subtle rounded-lg p-6 mb-6">
                                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                                        Consider setting up continuous monitoring to get alerted if new threats are detected.
                                    </p>

                                    {/* CTA for no results state */}
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                        <button
                                            onClick={onClose}
                                            className="px-6 py-2 text-text-secondary-light dark:text-text-secondary-dark font-medium border border-border-light dark:border-border-dark-subtle rounded-lg hover:bg-background-light-elevated dark:hover:bg-background-dark-secondary transition-all duration-200"
                                        >
                                            Close
                                        </button>
                                        <button
                                            onClick={handleBookConsultation}
                                            className="px-8 py-2 text-white font-medium bg-gradient-to-r from-gradient-start to-gradient-end rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 justify-center"
                                        >
                                            Set Up Monitoring
                                            <ExternalLink className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchResultsModal;